import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from "@angular/core";
import { FormArray, FormBuilder, FormControl } from "@angular/forms";
import { DeviceMetadataParser } from "@app/project/services/device-metadata-parser";
import { Subject } from "rxjs";
import { distinctUntilChanged, filter, takeUntil, tap } from "rxjs/operators";

@Component({
    selector: "generic-input-container",
    templateUrl: "./generic-input-container.component.html",
    styleUrls: ["./generic-input-container.component.scss"],
})
export class GenericInputContainerComponent implements OnInit, OnDestroy, OnChanges {
    @Input() metadataInputs: any;
    @Input() hideSubmit = false;
    @Input() hideLoading = false;
    @Input() presentationStyle: string;
    @Input() enableRetry: boolean;
    @Output() relayEmitter: EventEmitter<any> = new EventEmitter();
    @Output() getCurrentValueEmitter: EventEmitter<any> = new EventEmitter();
    @Input() expandedIndex;
    @Input() showSelectLabels = true;
    @Input() fullWidthSaveButton = false;
    @Input() checkboxRequired = false;

    private updateLastKnownGoodValues: EventEmitter<any> = new EventEmitter();

    public formMetadata = [];
    public formGroup = this.formBuilder.group({});
    public isLoading = false;
    public showSave = false;
    private lastKnownGoodValue = {};
    private unsubscribe$ = new Subject();

    constructor(public formBuilder: FormBuilder, public cdr: ChangeDetectorRef) {}

    ngOnInit() {
        const inputs = DeviceMetadataParser.getInputs(this.metadataInputs.setOperation || this.metadataInputs, true);
        inputs.forEach(input => this.buildFormFields(input));

        if (this.formMetadata.length > 1 && !this.hideSubmit) {
            this.showSave = true;
        } else {
            this.submitFormOnChanges();
        }

        this.subscribeToLastKnownGoodValues();
        this.cdr.detectChanges();
    }

    ngOnChanges(changes) {
        if (changes.expandedIndex && this.expandedIndex) {
            this.resetInputValue();
        }
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    buildFormFields(input) {
        // if the input is hardcoded, just add a form control to the form, no component
        // example: in zwave metadata we hardcode the values for IN1 and IN2, so these fall in here
        if (typeof input.value === "string") {
            this.addFieldToGroup(input.inputName, input.value);
            // if the input metadata is an object type, just add it to the form fields and let the HTML do the rest
        } else if (input.type === "object") {
            Object.values(input.inputs).forEach((value: any) => {
                this.addFieldToGroup(value.inputName);
                this.formMetadata.push(value);
            });
        } else {
            const _input = {
                ...input,
                inputName: input.inputName || this.metadataInputs.Name,
            };

            if (this.metadataInputs.Value !== undefined) {
                this.addFieldToGroup(_input.inputName, this.metadataInputs.Value);
            } else if (_input.Value) {
                this.addFieldToGroup(_input.inputName, _input.Value);
            } else if (_input.default !== undefined) {
                this.addFieldToGroup(_input.inputName, _input.default);
            } else {
                this.addFieldToGroup(_input.inputName, null);
            }
            const formField = this.formMetadata.find(field => field.inputName === _input.inputName);
            if (!formField) {
                this.formMetadata.push(_input);
            } else {
                formField.valuesArray = [
                    ...(formField.valuesArray || [{ name: formField.friendlyName, checked: formField.checked, value: formField.Value }]),
                    { name: _input.friendlyName, checked: _input.checked, value: _input.Value },
                ];
            }

            this.getInitialValue();
        }
    }

    resetInputValue() {
        if (this.formGroup.controls[this.metadataInputs.inputName]) {
            const formControl = this.formGroup.controls[this.metadataInputs.inputName];
            let formValue = null;
            if (this.metadataInputs.Value !== undefined) {
                formValue = this.metadataInputs.Value;
            }
            if (this.metadataInputs.Metadata.default !== undefined) {
                formValue = this.metadataInputs.Metadata.default;
                formControl.setValue(formValue);
            }

            if (formControl) {
                formControl.setValue(formValue, { emitEvent: false });
            }
        }
    }

    addFieldToGroup(name, value?, validators = []) {
        const frmCtrl = new FormControl(value, validators);

        if (!this.formGroup.controls[name]) {
            this.lastKnownGoodValue[name] = value;
            this.formGroup.addControl(name, frmCtrl);
        } else if (this.formGroup.controls[name]) {
            if (!(this.formGroup.controls[name] instanceof FormArray)) {
                // set control as array
                this.formGroup.controls[name] = new FormArray([this.formGroup.controls[name], frmCtrl]);
            } else {
                // add control to array
                (this.formGroup.controls[name] as FormArray).push(frmCtrl);
            }
        }

        return frmCtrl;
    }

    subscribeToLastKnownGoodValues() {
        this.updateLastKnownGoodValues.subscribe(result => {
            if (result) {
                // on success, update the last known good value
                this.lastKnownGoodValue = Object.assign({}, this.formGroup.value, result);
            }

            this.formGroup.enable({ emitEvent: false });
            this.formGroup.patchValue(this.lastKnownGoodValue);
            this.isLoading = false;
            this.cdr.detectChanges();
        });
    }

    getInitialValue() {
        // If there is a getter operation, fetch the initial values and emit to updateLastKnownGoodValues when done.
        if (this.metadataInputs.getOperation) {
            this.isLoading = true;
            this.formGroup.disable({ emitEvent: false });
            const eventData = {
                metadata: this.metadataInputs,
                emitter: this.updateLastKnownGoodValues,
            };

            return this.getCurrentValueEmitter.emit(eventData);
        }
    }

    saveForm() {
        if (this.formGroup.valid) {
            this.isLoading = this.hideLoading ? false : true;
            this.relayEmitter.emit({
                form: this.formGroup,
                emitter: this.updateLastKnownGoodValues,
                metadata: this.metadataInputs,
                disable: true,
            });
        } else {
            // Object.keys(this.formGroup.controls).forEach(field => {
            //     this.formGroup.get(field).markAsTouched({ onlySelf: true });
            // });
            this.relayEmitter.emit({
                form: this.formGroup,
                emitter: this.updateLastKnownGoodValues,
                metadata: this.metadataInputs,
                disable: true,
            });
        }
    }

    submitFormOnChanges() {
        this.formGroup.valueChanges
            .pipe(
                takeUntil(this.unsubscribe$),
                distinctUntilChanged(),
                filter(_ => !this.isLoading),
                // TODO: Delay this until the last initial form value is retrieved
                tap(_ => this.saveForm())
            )
            .subscribe();
    }

    isSlider(input) {
        return DeviceMetadataParser.isSlider(input);
    }

    isCheckbox(input) {
        return DeviceMetadataParser.isCheckbox(input);
    }

    isButtonToggle(input) {
        return DeviceMetadataParser.isLabeledToggle(input);
    }

    isSlideToggle(input) {
        return DeviceMetadataParser.isSlideToggle(input);
    }

    isSelect(input) {
        return DeviceMetadataParser.isSelect(input);
    }
}
